import { ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Modal } from "@next-platform/client-common-spicy-ui";
import { updateWaitingQueue, useAppDispatch, useAppSelector } from "@next-platform/client-common-core";
import { Image, Slider, Spacer } from "@nextui-org/react";
import { AnimatePresence, motion } from "framer-motion";
import type { Dispatch, SetStateAction } from "react";
import { JSX, useEffect, useState } from "react";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { FIRST_TIME_WAITING_QUEUE_MODAL_KEY, QueueStatus, ROUTES } from "@/constants";
import { typedLocalStorageService } from "@/utils/localStorageRe";

const waitingQueueImage = "/Assets/waitingQueue.svg";
const sendLogo = "/Assets/send.svg";
const MAX_MINUTES_SLIDER = 15;

type WaitingQueueModalProps = {
  showWaitingQueueModal: boolean;
  setShowWaitingQueueModal: Dispatch<SetStateAction<boolean>>;
};

function WaitingQueueModal({ showWaitingQueueModal, setShowWaitingQueueModal }: WaitingQueueModalProps): JSX.Element {
  const { status, position, time_left } = useAppSelector((state) => state.waitingQueue) || {};

  const minutesLeft = Math.floor(Number(time_left) / 60);

  const progressedWaitingTime = (minutesLeft / MAX_MINUTES_SLIDER) * 100;
  const dispatch = useAppDispatch();
  const [isModalExpanded, setIsModalExpanded] = useState(!typedLocalStorageService.getItem(FIRST_TIME_WAITING_QUEUE_MODAL_KEY));

  useEffect(() => {
    if (status === QueueStatus.EXPIRED) {
      dispatch(updateWaitingQueue({ status: QueueStatus.REFRESH }));
    }
  }, [status, dispatch]);

  const onCloseHandler = () => {
    setShowWaitingQueueModal(false);
    typedLocalStorageService.setItem(FIRST_TIME_WAITING_QUEUE_MODAL_KEY, "true");
  };
  return (
    <>
      <Modal open={status === "waiting" && showWaitingQueueModal} onOpenChange={onCloseHandler}>
        <Col align="center">
          <Image removeWrapper src={waitingQueueImage} />
          <div className="leading-[0]">
            <Text variant="regular">
              <Trans i18nKey="chat:modal.waitingQueue.title" />
            </Text>
          </div>
        </Col>
        <Col align="center">
          {minutesLeft > 1 && (
            <Text variant={"sm"}>
              <Trans i18nKey="chat:modal.waitingQueue.estimatedWaitTimeFormatted" values={{ minutes: minutesLeft }} components={{ b: <b /> }} />
            </Text>
          )}
          <Text variant={"sm"}>
            <Trans i18nKey="chat:modal.waitingQueue.positionInLineFormatted" values={{ position }} components={{ b: <b /> }} />
          </Text>
          <Spacer y={4} />
          <Slider
            className="px-2.5"
            value={progressedWaitingTime}
            classNames={{ track: "h-2", filler: "rounded-full" }}
            renderThumb={(props) => (
              <div {...props} className="bg-white size-5 rounded-full grid place-items-center top-1/2 shadow-md">
                <img src={sendLogo} alt="Send logo" className="size-3 mt-0.5" />
              </div>
            )}
          />
          <Spacer y={4} />
          <AnimatePresence>
            {isModalExpanded ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="grid gap-4 overflow-hidden text-center leading-4"
              >
                <Text variant="sm">
                  <Trans i18nKey="chat:modal.waitingQueue.explanation1" />
                </Text>
                <Text variant="sm">
                  <Trans i18nKey="chat:modal.waitingQueue.explanation2" />
                </Text>
                <Text variant="sm">
                  <Trans i18nKey="chat:modal.waitingQueue.explanation3" />
                </Text>
                <Text variant="sm">
                  <Trans i18nKey="chat:modal.waitingQueue.explanation4" />
                </Text>
              </motion.div>
            ) : (
              <Text variant="regular" type="medium">
                <Trans i18nKey="chat:modal.waitingQueue.tiredOfWaiting" />
              </Text>
            )}
          </AnimatePresence>
        </Col>
        <Col className="text-center gap-2">
          <Row className="w-full *:grow leading-4 *:rounded-lg">
            <SpicyButton as={LocalizedLink} color="primary_strong" variant="primary_large" to={ROUTES.subscribe}>
              <Text variant="md" type="white" className="text-wrap">
                <Trans i18nKey="chat:modal.waitingQueue.cta.upgrade" />
              </Text>
            </SpicyButton>
          </Row>
          {!isModalExpanded && (
            <Row className="cursor-pointer justify-center items-center gap-2">
              <Text variant="sm" type="blue" onClick={() => setIsModalExpanded(!isModalExpanded)} className="text-wrap">
                <Trans i18nKey="chat:modal.waitingQueue.learnMore" />
              </Text>
              <ButtonIcon
                icon={IconVariants.PLUS}
                className="bg-blue-600 w-4 h-4 text-gray-50 rounded-sm mt-0.5"
                size={ButtonSize.SMALL}
                onClick={() => setIsModalExpanded(!isModalExpanded)}
                variant={ButtonVariant.TRANSPARENT}
              />
            </Row>
          )}
        </Col>
      </Modal>
    </>
  );
}

export default WaitingQueueModal;
